import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Toaster } from "react-hot-toast";

//* ReactFlow Library
import ReactFlow, {
  useEdgesState,
  useNodesState,
  addEdge,
  Controls,
  MiniMap,
  applyNodeChanges,
  useReactFlow,
  Background,
  BackgroundVariant,
} from "reactflow";

//* Import Utilities and Helpers
import apiUtils from "../../utilities/apiUtils";
import nodeUtils from "./utilities/nodeUtils";
import undoRedoUtils from "./utilities/undoRedoUtils";
import {
  useResetStore,
  useTeamDataStore,
  useGameNumberStore,
  useDuplicateGameStore,
} from "../../utilities/stores";
import {
  getId,
  nodeTypes,
  edgesTypes,
  initialNode,
  generateNodesData,
  generateEdgesData,
  findFirstMissingNumber,
  removeDuplicateGames,
  getHelperLines,
  getAllTeamsKeyVal,
  defaultEdgeOptions,
  defaultViewport,
  proOptions,
  updateWinnerLoserValue,
  updateDropGameNumbers,
  getMaxValues,
  moveLayout,
  hoverToHighlight,
  longTextHover,
} from "./utilities/helper";

//* Components
import Loader from "../common/Loader";
import Sidebar from "./components/Sidebar";
import Actions from "./components/Actions";
import Menu from "./menu/Menu";
import HelperLines from "./nodes/features/HelperLines";
import GlobalFields from "./components/GlobalFields";
import LoadTemplateModal from "./components/LoadTemplateModal";
import Movingtitle from "./components/MovingTitle";

//* Import CSS/Style
import "react-confirm-alert/src/react-confirm-alert.css";
import { isEqual } from "lodash";

//* Import Context API
import { useBracketState } from "./context/BracketStateContext";
import HeaderPanel from "./components/HeaderPanel";

const Bracket = ({
  bracket_template_id,
  division = null,
  event_id = 0,
  event_name = "Bracket Template",
  can_save_to_skybox,
  has_working_team = false,
}) => {
  //* Context API
  const { templateInfo, setTemplateInfo, isLoading, setIsLoading } =
    useBracketState();

  //* Reactflow
  const { setViewport, screenToFlowPosition, getNode } = useReactFlow();

  //* Utilities
  const { labels, updateNodeData, updateUsedTitles, deleteWhatIfNode } =
    nodeUtils();
  const { undo, redo, canUndo, canRedo, takeSnapshot } = undoRedoUtils();
  const { loading, sendPOSTRequest, sendGETRequest } = apiUtils();

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  //* Stores
  const { setFormReset } = useResetStore();
  const { duplicateGames, setDuplicateGames } = useDuplicateGameStore();
  const { teamData, setTeamData } = useTeamDataStore();
  const { gameNumbers, setGameNumbers } = useGameNumberStore();

  const reactFlowWrapper = useRef(null);
  const connectingNodeData = useRef(null);

  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [nodeEdgesData, setNodeEdgesData] = useState({ nodes: [], edges: [] });
  const [initData, setinitData] = useState({
    nodes: [],
    edges: [],
    viewport: {},
  });

  const [menu, setMenu] = useState(null);
  const [helperLines, setHelperlines] = useState({
    horizontal: undefined,
    vertical: undefined,
  });

  const [selectedData, setSelectedData] = useState({ nodes: [], edges: [] });
  const [bracketID, setBracketID] = useState(0);
  const [allTeams, setAllTeams] = useState({});
  const [sidebarTeams, setSidebarTeams] = useState({});
  const [hideSeed, setHideSeed] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [canAdvanceTeams, setCanAdvanceTeams] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);

  const [teamList, setTeamList] = useState({ skybox: [], local: [] });
  const [activeTeam, setActiveTeam] = useState(0);
  const [pickedTemplate, setPickedTemplate] = useState({
    nodes: [],
    edges: [],
  });

  const [tempTemplateInfo, setTempTemplateInfo] = useState({});
  const [poolsStandings, setPoolsStandings] = useState([]);

  const ref = useRef(null);

  const onlyGameNodes = useMemo(() => {
    hoverToHighlight();
    longTextHover();

    return nodes
      .filter((item) => item.data.type !== "titleNode" && item.data.game_number)
      .map((item) => item.data.game_number)
      .join(",");
    // }, [nodes.length]);
  }, [nodes]);

  const getGameNumbers = useMemo(() => {
    return [
      ...new Set(
        nodes
          .filter((item) => item.data.game_number !== undefined)
          .map((item) => Number(item.data.game_number) || [])
      ),
    ].sort((a, b) => a - b);
  }, [nodes.length, onlyGameNodes]);

  useEffect(() => {
    const fetchInitData = async () => {
      await getTeamList();
      await getOptions();
      if (bracket_template_id && division == null) {
        await getTemplateDetails(bracket_template_id);
      } else if (division != null && event_id) {
        await getAllDivisions(division);
        await getAllTeams(division);
        await getDivisionTemplateDetails(division);
      }
    };

    fetchInitData();

    //* Remove tooltip from the minimap
    if (document.getElementById("react-flow__minimap-desc-1")) {
      document.getElementById("react-flow__minimap-desc-1").remove();
    }
  }, []);

  useEffect(() => {
    let maxNumber = Math.max(0, ...getGameNumbers);

    if (maxNumber > 0) {
      const nodeCount = nodes
        .filter((item) => item.data.game_number !== undefined)
        .map((item) => item.data.game_number || []);

      if (maxNumber < nodeCount.length) {
        maxNumber = maxNumber + (nodeCount.length - maxNumber);
      }

      const gameLength = Array.from(
        { length: maxNumber },
        (_, index) => index + 1
      );

      if (!isEqual(gameLength, gameNumbers)) {
        setGameNumbers(gameLength);
      }
    }
  }, [getGameNumbers]);

  useEffect(() => {
    const fetchData = async () => {
      if (teamData.length) {
        const hasWorkingTeam = division && has_working_team ? true : false;

        const arrNodes = await generateNodesData(
          initData.nodes,
          teamData,
          allTeams,
          false,
          false,
          true,
          false,
          Object.keys(sidebarTeams),
          hasWorkingTeam
        );

        const arrEdges = await generateEdgesData(initData.edges);
        setNodes(arrNodes);
        setEdges(arrEdges);

        if (Object.keys(initData.viewport).length > 0) {
          setViewport(initData.viewport);
        }

        setNodeEdgesData({ nodes: arrNodes, edges: arrEdges });
      }
    };

    fetchData();
  }, [initData]);

  //* API call for load the options.
  const getOptions = async () => {
    const { data } = await sendPOSTRequest(
      "manager/division_bracket_template_dropdownoptions",
      {
        id: division ? division : null,
      }
    );

    const teamDataVal = Object.values(data.team_type);
    const teamDataObj = teamDataVal.map((item) => {
      if (!division) {
        if (item.value === "division" || item.value === "overall") {
          return { ...item, maxTeams: 128 };
        }
      }
      return item;
    });
    // End

    //* Set value on the divisions template value in the popup dropdown
    const teamType = teamDataObj.find((item) => item.value === "division");

    //* Remove this condition if you want to load only  for the division.
    // if (teamType && teamType.maxTeams > 0 && division) {
    if (teamType && teamType.maxTeams > 0) {
      setActiveTeam(teamType.maxTeams);
    }
    //* End

    setTeamData(teamDataObj);
  };

  //* API call for load the teams list [SKYBOX]
  const getTeamList = async () => {
    const { data: skyboxListing } = await sendGETRequest(
      "brackets-template/teams",
      {},
      true
    );

    if (skyboxListing.status) {
      setTeamList((prev) => ({ ...prev, skybox: skyboxListing.data }));
    }

    const { data: localListing } = await sendGETRequest(
      "manager/bracket_template_listing_v2",
      {}
    );

    if (localListing.success) {
      setTeamList((prev) => ({ ...prev, local: localListing.data }));
    }
  };

  //* API Call for load the division dropdown.
  const getAllDivisions = async (event_price_id) => {
    const { data } = await sendPOSTRequest("manager/event_alldivisions", {
      id: event_price_id,
    });

    if (data) {
      setDivisions(data);
    }
  };

  //* API calls for load the react data.
  const loadTemplateDetail = async (templateID) => {
    const { data } = await sendPOSTRequest("manager/bracket_template_detail", {
      bracket_template_id: templateID,
    });

    return data;
  };

  const getTemplateDetails = async (templateID) => {
    const data = await loadTemplateDetail(templateID);

    if (data.success) {
      const {
        bracket_id,
        brackets,
        edges = [],
        template_id,
        template_name,
        teams,
        bracket_type,
        titles = [],
        viewport = null,
      } = data.template;

      setTemplateInfo({
        id: template_id,
        name: template_name,
        teams,
        bracket_type,
      });

      setBracketID(bracket_id);

      if (brackets) {
        const firstBrackets = brackets[0];

        if (firstBrackets && firstBrackets.bracket_id) {
          const nodesData = brackets[0] ? brackets[0].matchups[0] : [];

          await updateUsedTitles(nodesData, true);

          setinitData({
            nodes: [...nodesData, ...titles],
            edges: edges,
            viewport: viewport ? JSON.parse(viewport) : {},
          });
        } else {
          setModalOpen(true);
        }
      } else {
        setModalOpen(true);
      }
    } else {
      window.location.href = `${window.APP_URL}manager/bracket_template#/bracket_template`;
    }
  };

  const getDivisionTemplateDetails = async (event_price_id) => {
    const { data } = await sendPOSTRequest(
      "manager/division_bracket_template_loadtemplatebydivision",
      {
        id: event_price_id,
      }
    );

    setPoolsStandings(data.pools || []);

    if (data.success) {
      const {
        brackets,
        edges = [],
        titles = [],
        viewport = null,
        can_advnace_teams = 0,
      } = data.template;

      setHideSeed(data.hide_bracket_seed);

      if (!brackets.length) {
        setModalOpen(true);
      }

      setCanAdvanceTeams(can_advnace_teams);

      if (brackets) {
        const firstBrackets = brackets[0];

        if (firstBrackets && firstBrackets.bracket_id) {
          let nodesData;

          if (brackets[0].matchups.length > 1) {
            nodesData = brackets[0]
              ? brackets[0].matchups.map((item) => item[0])
              : [];
          } else {
            nodesData = brackets[0] ? brackets[0].matchups[0] : [];
          }

          if (titles && edges) {
            await updateUsedTitles(nodesData, true);

            const mergeNodes = [...nodesData, ...titles];

            if (!mergeNodes.length) {
              setModalOpen(true);
            }

            setinitData({
              nodes: mergeNodes,
              edges: edges,
              viewport: viewport ? JSON.parse(viewport) : {},
            });
          }
        }
      }
    } else {
      setModalOpen(true);
    }
  };

  const getAllTeams = async (event_price_id) => {
    const { data } = await sendPOSTRequest(
      "manager/event_registration_item_allteamsbydivisionlevel",
      {
        id: event_price_id,
      }
    );

    const allTeamData = getAllTeamsKeyVal(data);
    setAllTeams(allTeamData);

    const getActiveTeams = getAllTeamsKeyVal(data, 1);
    setSidebarTeams(getActiveTeams);
  };

  //* Reset Flow
  const resetData = async () => {
    await setNodes(nodeEdgesData.nodes);
    await setEdges(nodeEdgesData.edges);
    await setFormReset(true);

    //* Update the Dropped nodes store.
    await updateUsedTitles(nodeEdgesData.nodes);

    //* Update the duplicate games store.
    const removedGames = await removeDuplicateGames(duplicateGames);
    await setDuplicateGames(removedGames);
  };

  //* Reload flow when the data is saved.
  const reloadData = (data) => {
    if (data.bracket_id) {
      setBracketID(data.bracket_id);
    }

    setinitData({
      nodes: [...data.matchups, ...data.titles],
      edges: data.edges,
      viewport: {},
    });

    if (data.template_id && data.template_name) {
      setTemplateInfo({
        ...templateInfo,
        id: data.template_id,
        name: data.template_name,
      });
    }
  };

  const applyHelperLine = useCallback((changes, nodes) => {
    setHelperlines({
      horizontal: undefined,
      vertical: undefined,
    });

    if (
      changes.length === 1 &&
      changes[0].type === "position" &&
      changes[0].dragging &&
      changes[0].position
    ) {
      const helperLines = getHelperLines(changes[0], nodes);
      changes[0].position.x =
        helperLines.snapPosition.x ?? changes[0].position.x;
      changes[0].position.y =
        helperLines.snapPosition.y ?? changes[0].position.y;

      setHelperlines({
        horizontal: helperLines.horizontal,
        vertical: helperLines.vertical,
      });
    }

    return applyNodeChanges(changes, nodes);
  }, []);

  //** ReactFlow Methods */
  const onConnect = useCallback(
    (connection) => {
      takeSnapshot();
      setEdges((edges) => addEdge(connection, edges));
      connectingNodeData.current = null;
    },
    [setEdges, takeSnapshot]
  );

  const onConnectStart = useCallback((_, { nodeId, handleType, handleId }) => {
    const nodeData = getNode(nodeId);
    if (nodeData.type == "defaultNode" || nodeData.type == "compassNode") {
      connectingNodeData.current = { nodeId, handleType, handleId };
    }
  }, []);

  const onConnectEnd = useCallback(
    ({ clientX, clientY, target }) => {
      if (!connectingNodeData.current) return;

      const { nodeId, handleType, handleId } = connectingNodeData.current;
      const targetIsPane = target.classList.contains("react-flow__pane");
      const nodeData = getNode(nodeId);

      const connectEnd = async () => {
        const id = getId();
        const { newNumber } = await findFirstMissingNumber(getGameNumbers);

        let xValue = 0;
        const hasWorkingTeam = division && has_working_team ? true : false;

        const workingTeamFields = hasWorkingTeam
          ? {
              hasWorkingTeam: hasWorkingTeam,
              team_3_of_type: "",
              team_3_of_type_label: "",
              team_3_of_type_value: "",
              team_3_cancelled: true,
            }
          : {};

        if (nodeData.type == "compassNode" && handleId == "source-a") {
          xValue = nodeData.width * 0.4;
        } else if (nodeData.type == "defaultNode") {
          if (
            (nodeData.sourcePosition == "left" && handleType == "source") ||
            (nodeData.sourcePosition == "right" && handleType != "source")
          ) {
            xValue = nodeData.width * 0.4;
          }
        }

        let newNode = {
          id,
          type: "defaultNode",
          data: { ...initialNode.data, ...workingTeamFields },
          position: screenToFlowPosition({
            x: clientX - xValue,
            y: clientY,
          }),
          selected: true,
        };

        let newEdge = {};

        if (nodeData.type == "compassNode") {
          newNode.sourcePosition = handleId == "source-a" ? "left" : "right";
          newNode.targetPosition = handleId == "source-a" ? "right" : "left";
        } else {
          newNode.sourcePosition =
            nodeData.sourcePosition == "left" ? "left" : "right";
          newNode.targetPosition =
            nodeData.sourcePosition == "left" ? "right" : "left";
        }

        if (handleType == "source") {
          const labelType =
            nodeData.type === "compassNode" && handleId === "source-a"
              ? "loser"
              : "winner";

          newNode.data = {
            ...newNode.data,
            team_1_of_type: labelType,
            team_1_of_type_label: `${labels[labelType]} ${nodeData.data.game_number}`,
            team_1_of_type_value: nodeData.data.game_number,
            game_number: newNumber,
          };

          newEdge = {
            id,
            source: nodeId,
            sourceHandle: handleId,
            target: id,
          };
        } else {
          newNode.data = {
            ...newNode.data,
            game_number: newNumber,
          };

          newEdge = {
            id,
            source: id,
            target: nodeId,
            targetHandle: handleId,
          };

          const teamNumber = handleId == "target-a" ? "1" : "2";

          if (!nodeData.data[`team_${teamNumber}_of_type`]) {
            const newData = {
              [`team_${teamNumber}_of_type`]: "winner",
              [`team_${teamNumber}_of_type_label`]: `${labels["winner"]} ${newNumber}`,
              [`team_${teamNumber}_of_type_value`]: newNumber,
              newGenerated: true,
            };
            await updateNodeData(nodeId, newData);
          }
        }

        await setNodes((nodes) => nodes.concat(newNode));
        await setEdges((edges) => edges.concat(newEdge));
        updateUsedTitles();
      };

      const hasSource = edges.some(
        (item) => item.source === nodeId && item.sourceHandle === handleId
      );

      const hasTarget = edges.some(
        (item) => item.target === nodeId && item.targetHandle === handleId
      );

      let sameSource = true;
      const hasSameSource = edges.some((item) => item.source === nodeId);
      if (
        nodeData.type != "compassNode" &&
        handleType == "source" &&
        hasSameSource
      ) {
        sameSource = false;
      }

      if (
        targetIsPane &&
        !hasSource &&
        !hasTarget &&
        sameSource &&
        (nodeData.type == "defaultNode" || nodeData.type == "compassNode")
      ) {
        connectEnd();
      }

      connectingNodeData.current = null;
    },
    [screenToFlowPosition, getGameNumbers, updateUsedTitles, edges]
  );

  const onNodeDragStart = useCallback(() => takeSnapshot(), [takeSnapshot]);

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onSelectionChange = (data) => {
    const nodes = data.nodes;
    if (!isEqual(nodes, selectedData.nodes)) {
      setSelectedData({ nodes, edges: data.edges });
    }
  };

  const onSelectionDragStart = useCallback(
    () => takeSnapshot(),
    [takeSnapshot]
  );

  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nodes) => applyHelperLine(changes, nodes));
    },
    [setNodes, applyHelperLine]
  );

  const onNodesDelete = async (event) => {
    takeSnapshot();

    const idsToRemove = event.map((item) => item.id);

    if (idsToRemove.length) {
      //* Delete `whatIfNode` if the parent is deleted
      const whatIfNodes = await deleteWhatIfNode(idsToRemove, true);
      setNodes((nodes) =>
        nodes.filter((node) => !whatIfNodes.includes(node.id))
      );
    }

    if (idsToRemove.length && duplicateGames) {
      idsToRemove.forEach((key) => delete duplicateGames[key]);

      //* Remove if the single game number are available.
      const removedGames = await removeDuplicateGames(duplicateGames);
      await setDuplicateGames(removedGames);

      //* Update the Dropped nodes store.
      const newNodes = nodes.filter((item) => !idsToRemove.includes(item.id));
      await updateUsedTitles(newNodes);
    }

    const selectedGames = [
      ...new Set(
        event
          .filter((item) => item.type !== "titleNode" && item.data.game_number)
          .map((item) => item.data.game_number) || []
      ),
    ];

    //* Remove node to remove the winner and loser
    if (selectedGames.length) {
      const teamTypes = ["winner", "loser"];

      setNodes((nodes) =>
        nodes.map((node) => {
          const nodeData = { ...node.data, newGenerated: true };

          const updateTeamType = (type) => {
            if (
              teamTypes.includes(nodeData[type]) &&
              selectedGames.includes(nodeData[`${type}_value`])
            ) {
              nodeData[type] = "";
              nodeData[`${type}_label`] = "";
              nodeData[`${type}_value`] = "";
            }
          };

          updateTeamType("team_1_of_type");
          updateTeamType("team_2_of_type");

          return { ...node, data: nodeData };
        })
      );
    }
  };

  const onEdgesDelete = useCallback(
    (event) => {
      takeSnapshot();
    },
    [takeSnapshot]
  );

  const onPaneClick = useCallback(
    (event) => {
      //* Get pointer click coordination.
      const newPosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (pickedTemplate.nodes.length) {
        //* Change the template_name, bracket_type and teams value.
        setTemplateInfo({ ...tempTemplateInfo });

        //* Move layout to the x:0 and y:0 if no 2nd argument then.
        const updatedLayoutNodes = moveLayout(
          pickedTemplate.nodes,
          newPosition
        );

        setNodes((nodes) => {
          const newData = [...nodes, ...updatedLayoutNodes];
          updateUsedTitles(newData);
          return newData;
        });

        if (pickedTemplate.edges.length) {
          setEdges((edges) => [...edges, ...pickedTemplate.edges]);
        }

        setPickedTemplate({
          nodes: [],
          edges: [],
        });

        setTempTemplateInfo({});
      }

      setMenu(null);
      takeSnapshot();
    },
    [
      setMenu,
      takeSnapshot,
      pickedTemplate,
      setPickedTemplate,
      setTemplateInfo,
      tempTemplateInfo,
    ]
  );

  //* Prevent the wrong edge connections
  const isValidConnection = ({
    source,
    sourceHandle,
    target,
    targetHandle,
  }) => {
    const sourceNode = getNode(source);
    const targetNode = getNode(target);

    if (sourceNode.type == "compassNode" || targetNode.type == "compassNode") {
      if (
        (sourceHandle == "source-b" && targetNode.sourcePosition == "right") ||
        (sourceHandle == "source-a" && targetNode.sourcePosition == "left")
      ) {
        const hasElement = edges.some(
          (item) => item.source === source && item.sourceHandle === sourceHandle
        );

        if (hasElement) {
          return false;
        }

        return true;
      }
      return false;
    }

    // const hasSameSource = edges.some((item) => item.source === source);

    // if (hasSameSource) {
    //   return false;
    // }

    // const hasTargetSource = edges.some(
    //   (item) => item.source === target && item.target === source
    // );

    // if (hasTargetSource) {
    //   return false;
    // }

    // const hasSource = edges.some(
    //   (item) => item.source === source && item.sourceHandle === sourceHandle
    // );

    // console.log("hasSource => ", hasSource);

    // const hasTarget = edges.some(
    //   (item) => item.target === target && item.targetHandle === targetHandle
    // );

    // console.log("hasTarget => ", hasTarget);

    // if (!hasSource && hasTarget) {
    //   return false;
    // }

    // return !hasSource;

    const isSameHandleConnect = edges.some(
      (edge) => edge.source === source && edge.target === target
    );

    if (isSameHandleConnect) return false;

    const isSameTarget = edges.some(
      (edge) => edge.target === target && edge.targetHandle === targetHandle
    );

    if (isSameTarget) return false;

    const isCrossConnection = edges.some(
      (edge) =>
        edge.source === target &&
        edge.target == source &&
        edge.sourceHandle == sourceHandle
    );

    if (isCrossConnection) return false;

    return true;
  };
  //** End */

  //** Menu methods */
  const onContextMenu = useCallback(
    (event, nodes, type) => {
      event.preventDefault();
      const pane = ref.current.getBoundingClientRect();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();

      const hasWorkingTeam = division && has_working_team ? true : false;

      let menuData = {
        node: nodes,
        top: event.clientY - reactFlowBounds.top,
        left: event.clientX - reactFlowBounds.left,
        gameNumbers: getGameNumbers,
        hasWorkingTeam,
      };

      if (pane && reactFlowBounds) {
        if (type == "pane") {
          menuData = {
            ...menuData,
            pane: true,
          };
        } else {
          menuData = {
            ...menuData,
            node: type == "single" ? nodes : {},
            selectedData,
            gameNumbers: getGameNumbers,
          };
        }

        setMenu(menuData);
      }
    },
    [setMenu, ref, selectedData, getGameNumbers]
  );
  //** End */

  //* Load from template click
  const loadFromTemplate = (e) => {
    const filteredNodesCount = nodes.filter(
      (node) => node.type !== "titleNode"
    ).length;

    setActiveTeam(filteredNodesCount);
    setModalOpen(e);
  };

  //** Selected template from the load from template */
  const selectedTemplate = async (data) => {
    let newNodes = [];
    let newEdges = [];

    if (data.bracket_template_id) {
      const { data: skyboxTemplate } = await sendGETRequest(
        `brackets-template/template/${data.bracket_template_id}`,
        {},
        true
      );

      if (skyboxTemplate.status) {
        setTempTemplateInfo({
          id: 0,
          name: skyboxTemplate.data.template_name,
          teams: skyboxTemplate.data.teams,
          bracket_type: skyboxTemplate.data.bracket_type,
        });

        newNodes = [
          ...skyboxTemplate.data.matchups,
          ...skyboxTemplate.data.titles,
        ];
        newEdges = skyboxTemplate.data.edges;
      }
    } else {
      if (data.value) {
        const { template: localTemplate, success } = await loadTemplateDetail(
          data.value
        );

        if (success) {
          if (localTemplate.brackets) {
            const nodesData = localTemplate.brackets[0]
              ? localTemplate.brackets[0].matchups[0]
              : [];

            newNodes = [...nodesData, ...localTemplate.titles];
            newEdges = localTemplate.edges;
          }
        }
      }
    }

    let newNodeIds;
    let newGameNumbers = {};

    if (newNodes.length) {
      //* Set the maximum division value.

      const maxValue = await getMaxValues(newNodes);

      const getMaxLabelValue = (label) => {
        const labelValue = teamData.find((team) => team.value === label);
        return labelValue ? labelValue.maxTeams : 0;
      };

      const teamLabelValue = (team, label) => {
        const maxVal = getMaxLabelValue(label);
        return {
          ...team,
          maxTeams: maxValue.division > maxVal ? maxVal : team.maxTeams,
        };
      };

      const updatedTeamData = teamData.map((team) => {
        if (team.value === "division") {
          return teamLabelValue(team, "division");
        }

        if (team.value === "overall") {
          return teamLabelValue(team, "division");
        }
        return team;
      });

      setTeamData(updatedTeamData);
      //** End */

      let maxGameNumber = Math.max(0, ...getGameNumbers) + 1;

      //* Update the dropped all nodes game number
      const updatedValue = await updateDropGameNumbers(newNodes, maxGameNumber);

      newNodeIds = updatedValue.newNodeIds;
      newNodes = updatedValue.newNodes;
      newGameNumbers = updatedValue.newGameNumbers;

      //* Update Winner/Loser's value
      const updatedNodes = await updateWinnerLoserValue(
        newNodes,
        newGameNumbers
      );

      newNodes = await generateNodesData(
        updatedNodes,
        teamData,
        allTeams,
        false,
        true,
        false
      );
    }

    if (newEdges.length) {
      newEdges.forEach((edge) => {
        edge.source =
          newNodeIds.nodes[edge.source] ||
          newNodeIds.titles[edge.source] ||
          edge.source;
        edge.target =
          newNodeIds.nodes[edge.target] ||
          newNodeIds.titles[edge.target] ||
          edge.target;
      });

      newEdges = await generateEdgesData(newEdges, true, true);
    }

    setPickedTemplate({ nodes: newNodes, edges: newEdges });

    setModalOpen(false);
  };

  return (
    <>
      <div className="container-fluid bracketWrapper">
        <HeaderPanel
          event_id={event_id}
          event_name={event_name}
          division={division}
          divisions={divisions}
          bracket_template_id={bracket_template_id}
          hasNodes={initData.nodes.length > 0 ? true : false}
        />
        <div className="row bodyWrapper">
          <Sidebar
            division={division}
            teamData={teamData}
            gameNumbers={getGameNumbers}
            allTeams={sidebarTeams}
            poolsStandings={poolsStandings}
          />
          <div className="col-md-10">
            <div className="row">
              <div className="col-md-12 header-button">
                <Actions
                  division={division}
                  resetData={resetData}
                  reloadData={reloadData}
                  bracketID={bracketID}
                  hideSeed={hideSeed}
                  selectedData={selectedData}
                  canUndo={canUndo}
                  undo={undo}
                  canRedo={canRedo}
                  redo={redo}
                  loadTemplate={loadFromTemplate}
                  realoadTeamList={getTeamList}
                  canAdvanceTeams={canAdvanceTeams}
                  can_save_to_skybox={can_save_to_skybox}
                />
              </div>
              <div className="col-md-12 wrapperDiv">
                <div className="nodeWrapper" ref={reactFlowWrapper}>
                  <ReactFlow
                    proOptions={proOptions}
                    ref={ref}
                    nodes={nodes}
                    edges={edges}
                    onEdgesChange={onEdgesChange}
                    onNodesChange={onNodesChange}
                    onConnect={onConnect}
                    onConnectStart={onConnectStart}
                    onConnectEnd={onConnectEnd}
                    onNodeDragStart={onNodeDragStart}
                    onDragOver={onDragOver}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgesTypes}
                    onPaneClick={onPaneClick}
                    onNodeContextMenu={(event, node) =>
                      onContextMenu(event, node, "single")
                    }
                    defaultViewport={defaultViewport}
                    onNodesDelete={onNodesDelete}
                    onEdgesDelete={onEdgesDelete}
                    onSelectionChange={onSelectionChange}
                    onSelectionContextMenu={(event, nodes) =>
                      onContextMenu(event, nodes, "multiple")
                    }
                    onSelectionDragStart={onSelectionDragStart}
                    onPaneContextMenu={(event) =>
                      onContextMenu(event, {}, "pane")
                    }
                    defaultEdgeOptions={defaultEdgeOptions}
                    panOnScroll
                    minZoom={-Infinity}
                    isValidConnection={isValidConnection}
                  >
                    <Controls />
                    <MiniMap style={{ height: 120 }} zoomable pannable />
                    <Background variant={BackgroundVariant.Dots} />
                    <HelperLines {...helperLines} />
                    {menu && <Menu onClick={onPaneClick} {...menu} />}
                    {division == null && <GlobalFields />}
                  </ReactFlow>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <LoadTemplateModal
          onClose={() => setModalOpen(false)}
          teamList={teamList}
          activeTeam={activeTeam}
          pickTemplate={selectedTemplate}
        />
      )}
      {isLoading && <Loader />}
      <Toaster position="top-right" reverseOrder={false} />

      {pickedTemplate.nodes.length > 0 && (
        <Movingtitle
          className="nodeWrapper"
          title="Click here to load your selected template"
        />
      )}
    </>
  );
};

export default Bracket;
